import { useContext, useEffect, useReducer, useRef, useState } from 'react'

import {
  Button,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core'

import {
  BotIcon,
  MessageCircleMore,
  Pencil,
  PencilIcon,
  Trash2Icon
} from 'lucide-react'
import { toast } from 'react-toastify'
import ClosingReasonModal from '../../components/ClosingReasonModal/ClosingReasonModal'
import ConfirmationModal from '../../components/ConfirmationModal'
import DepartmentModal from '../../components/DepartmentModal'
import { DepartmentsFlow } from '../../components/DepartmentsFlow'
import { InputSwitch } from '../../components/InputSwitch'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import ProtectedRoute from '../../components/ProtectedRoute'
import QueueModal from '../../components/QueueModal'
import QuickTable from '../../components/QuickTable'
import TabPanel from '../../components/TabPanel'
import { SocketContext } from '../../context/Socket/SocketContext'
import { WhatsAppsContext } from '../../context/WhatsApp/WhatsAppsContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './styles'
import { ButtonAddNew } from '../../components/ButtonAddNew'
import useClosingReasonSettings from '../../hooks/useClosingReasonSettings'

const queuesReducer = (state, action) => {
  if (action.type === 'LOAD_QUEUES') {
    const queues = action.payload
    const newQueues = []

    queues.forEach(queue => {
      const queueIndex = state.findIndex(q => q.id === queue.id)
      if (queueIndex !== -1) {
        state[queueIndex] = queue
      } else {
        newQueues.push(queue)
      }
    })

    return [...state, ...newQueues]
  }

  if (action.type === 'UPDATE_QUEUES') {
    const queue = action.payload
    const queueIndex = state.findIndex(u => u.id === queue.id)

    if (queueIndex !== -1) {
      state[queueIndex] = queue
      return [...state]
    } else {
      return [queue, ...state]
    }
  }

  if (action.type === 'DELETE_QUEUE') {
    const queueId = action.payload
    const queueIndex = state.findIndex(q => q.id === queueId)
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const closingReasonsReducer = (state, action) => {
  if (action.type === 'LOAD_CLOSING_REASONS') {
    const closingReasons = action.payload
    const newClosingReasons = []

    closingReasons.forEach(closingReason => {
      const closingReasonIndex = state.findIndex(q => q.id === closingReason.id)
      if (closingReasonIndex !== -1) {
        state[closingReasonIndex] = closingReason
      } else {
        newClosingReasons.push(closingReason)
      }
    })

    return [...state, ...newClosingReasons]
  }

  if (action.type === 'UPDATE_CLOSING_REASON') {
    const closingReason = action.payload
    const closingReasonIndex = state.findIndex(u => u.id === closingReason.id)

    if (closingReasonIndex !== -1) {
      state[closingReasonIndex] = closingReason
      return [...state]
    } else {
      return [closingReason, ...state]
    }
  }

  if (action.type === 'DELETE_CLOSING_REASON') {
    const closingReasonId = action.payload

    const closingReasonIndex = state.findIndex(q => q.id === closingReasonId)
    if (closingReasonIndex !== -1) {
      state.splice(closingReasonIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const Departments = () => {
  const classes = useStyles()
  // const history = useHistory()

  const { requireClosingReason } = useClosingReasonSettings()

  const [queues, dispatch] = useReducer(queuesReducer, [])
  const [loading, setLoading] = useState(false)

  const [selectedBotId, setSelectedBotId] = useState(null)

  const [departmentModalOpen, setDepartmentModalOpen] = useState(false)
  const [selectedDepartment, setSelectedDepartment] = useState(null)

  const [queueModalOpen, setQueueModalOpen] = useState(false)
  const [selectedQueue, setSelectedQueue] = useState(null)

  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [showOnlyLevel1, setShowOnlyLevel1] = useState(() => {
    const saved = localStorage.getItem('showOnlyLevel1')
    return saved !== null ? JSON.parse(saved) : false
  })
  const [showAllBots, setShowAllBots] = useState(false)

  const [isMainContentHidden, setIsMainContentHidden] = useState(false)
  const buttonRef = useRef(null)

  const departmentContainerRef = useRef(null)
  const [, setShowNavigation] = useState(false)
  const [editingFlowQueueId, setEditingFlowQueueId] = useState(null)

  const socketManager = useContext(SocketContext)
  const { whatsApps } = useContext(WhatsAppsContext)

  const [botsWithWhatsApp, setBotsWithWhatsApp] = useState([])

  const [closingReasonModalOpen, setClosingReasonModalOpen] = useState(false)
  const [selectedClosingReason, setSelectedClosingReason] = useState(null)

  const [tab, setTab] = useState('bots')

  const activeTab = requireClosingReason ? tab : 'bots'

  useEffect(() => {
    if (!requireClosingReason && tab === 'closingReasons') {
      setTab('bots')
    }
  }, [requireClosingReason, tab])

  const [closingReasons, dispatchClosingReasons] = useReducer(
    closingReasonsReducer,
    []
  )
  const [deletingClosingReason, setDeletingClosingReason] = useState(null)
  const [searchParam, setSearchParam] = useState('')
  const [hasMore, setHasMore] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [closingReasonsLoading, setClosingReasonsLoading] = useState(false)

  useEffect(() => {
    if (tab === 'closingReasons' && requireClosingReason) {
      dispatchClosingReasons({ type: 'RESET' })
      setPageNumber(1)
    }
  }, [tab, searchParam, requireClosingReason])

  useEffect(() => {
    if (tab === 'closingReasons' && requireClosingReason) {
      setClosingReasonsLoading(true)
      const delayDebounceFn = setTimeout(() => {
        fetchClosingReasons()
      }, 500)
      return () => clearTimeout(delayDebounceFn)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber, tab, requireClosingReason])

  const fetchClosingReasons = async () => {
    try {
      const { data } = await api.get('/closing-reasons', {
        params: { searchParam, pageNumber }
      })

      dispatchClosingReasons({
        type: 'LOAD_CLOSING_REASONS',
        payload: data.closingReasons
      })
      setHasMore(data.hasMore)
      setClosingReasonsLoading(false)
    } catch (err) {
      toastError(err)
    }
  }

  const columns = [
    {
      field: 'id',
      headerName: i18n.t('closingReasons.table.id'),
      align: 'center'
    },
    {
      field: 'name',
      headerName: i18n.t('closingReasons.table.name')
    },
    {
      field: 'description',
      headerName: i18n.t('closingReasons.table.description')
    },
    // {
    //   field: 'departments',
    //   headerName: i18n.t('closingReasons.table.departments')
    // },
    {
      field: 'status',
      headerName: i18n.t('closingReasons.table.status'),
      align: 'center'
    }
  ]

  const customCellRenderer = {
    // departments: row => {
    //   return row.queues && row.queues.length > 0
    //     ? row.queues.map(queue => queue.name).join(', ')
    //     : 'Nenhum departamento'
    // },
    status: row => {
      return row.isActive
        ? i18n.t('closingReasons.table.active')
        : i18n.t('closingReasons.table.inactive')
    }
  }

  const customActionsRenderer = row => (
    <>
      <Tooltip title="Editar" arrow>
        <IconButton
          size="small"
          onClick={() => handleEditClosingReason(row)}
          className={classes.buttonActionEdit}
        >
          <PencilIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Excluir conexão" arrow>
        <IconButton
          size="small"
          onClick={() => {
            setConfirmModalOpen(true)
            setDeletingClosingReason(row)
          }}
          className={classes.buttonActionDelete}
        >
          <Trash2Icon />
        </IconButton>
      </Tooltip>
    </>
  )

  const handleDeleteClosingReason = async closingReasonId => {
    try {
      await api.delete(`/closing-reasons/${closingReasonId}`)
      toast.success(i18n.t('closingReasons.toasts.deleted'))
    } catch (err) {
      toastError(err)
    }
    setDeletingClosingReason(null)
    dispatchClosingReasons({
      type: 'DELETE_CLOSING_REASON',
      payload: closingReasonId
    })
    setSearchParam('')
  }

  const handleScroll = e => {
    if (!hasMore || closingReasonsLoading) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore()
    }
  }

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1)
  }

  const handleTabChange = (event, newValue) => {
    setTab(newValue)
  }

  useEffect(() => {
    const botQueues = queues.filter(queue => queue.isBot)

    let queueIdToWhatsApp = {}
    whatsApps.forEach(whatsapp => {
      if (Array.isArray(whatsapp.queues)) {
        whatsapp.queues.forEach(q => {
          queueIdToWhatsApp[q.id] = {
            id: whatsapp.id,
            number: whatsapp.name
          }
        })
      }
    })
    const finalBotsList = botQueues.map(bot => {
      return {
        ...bot,
        whatsapp: queueIdToWhatsApp[bot.id]
      }
    })
    setBotsWithWhatsApp(finalBotsList)
  }, [queues, whatsApps])

  useEffect(() => {
    if ((queueModalOpen || departmentModalOpen) && buttonRef.current) {
      buttonRef.current.blur()
    }
  }, [queueModalOpen, departmentModalOpen])

  useEffect(() => {
    fetchQueues()
  }, [])

  useEffect(() => {
    localStorage.setItem('showOnlyLevel1', JSON.stringify(showOnlyLevel1))
  }, [showOnlyLevel1])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const onQueue = data => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_QUEUES', payload: data.queue })
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_QUEUE', payload: data.queueId })
      }
    }

    socket.on(`company-${companyId}-queue`, onQueue)

    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  const fetchQueues = async () => {
    setLoading(true)
    try {
      const { data: queuesData } = await api.get('/queue')

      const fetchOptionsRecursive = async (queueId, parentId) => {
        const { data: optionsData } = await api.get('/queue-options', {
          params: { queueId, parentId }
        })
        const optionsWithChildren = await Promise.all(
          optionsData.map(async option => {
            const children = await fetchOptionsRecursive(queueId, option.id)
            return { ...option, children }
          })
        )
        return optionsWithChildren
      }

      const queuesWithChildren = await Promise.all(
        queuesData.map(async queue => {
          const options = await fetchOptionsRecursive(queue.id, -1)
          return { ...queue, options }
        })
      )

      dispatch({ type: 'LOAD_QUEUES', payload: queuesWithChildren })
      setLoading(false)
    } catch (err) {
      toastError(err)
      setLoading(false)
    }
  }

  const handleSelectBot = botId => {
    setSelectedBotId(prevId => {
      const newId = prevId === botId ? null : botId
      return newId
    })
    setEditingFlowQueueId(null)
  }

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true)
    setSelectedQueue(null)
    setIsMainContentHidden(true)
  }

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false)
    setSelectedQueue(null)
    setIsMainContentHidden(false)
    fetchQueues()

    // setShowOnlyLevel1(false)
    setSelectedBotId(null)
    setEditingFlowQueueId(null)
  }

  const handleOpenDepartmentModal = () => {
    setDepartmentModalOpen(true)
    setSelectedDepartment(null)
    setIsMainContentHidden(true)
  }

  const handleCloseDepartmentModal = () => {
    setDepartmentModalOpen(false)
    setSelectedDepartment(null)
    setIsMainContentHidden(false)
    fetchQueues()
  }

  const handleOpenClosingReasonModal = () => {
    setSelectedClosingReason(null)
    setClosingReasonModalOpen(true)
    setIsMainContentHidden(true)
  }

  const handleCloseClosingReasonModal = success => {
    if (success) {
      dispatchClosingReasons({ type: 'RESET' })
      if (pageNumber === 1) {
        setClosingReasonsLoading(true)
        fetchClosingReasons()
      } else {
        setPageNumber(1)
      }
    }
    setSelectedClosingReason(null)
    setClosingReasonModalOpen(false)
    setIsMainContentHidden(false)
  }

  const handleEditQueue = queue => {
    setSelectedQueue(queue)
    setQueueModalOpen(true)
  }

  const handleEditFlow = queue => {
    // history.push(`/departments-flow/${queue.id}`)
    setEditingFlowQueueId(queue.id)
  }

  const checkOverflow = () => {
    const container = departmentContainerRef.current
    if (container) {
      setShowNavigation(container.scrollWidth > container.clientWidth)
    }
  }

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false)
    setSelectedQueue(null)
    setDeletingClosingReason(null)
  }

  const handleEditDepartment = queue => {
    setSelectedDepartment(queue)
    setDepartmentModalOpen(true)
  }

  const handleDeleteQueue = async queueId => {
    try {
      await api.delete(`/queue/${queueId}`)
      toast.success(i18n.t('Queue deleted successfully!'))
    } catch (err) {
      toastError(err)
    }
    setSelectedQueue(null)
    fetchQueues()
  }

  // const handleNivelChange = async (queueId, newNivel) => {
  //   try {
  //     const { data } = await api.put(`/queue/${queueId}`, {
  //       nivel: newNivel.toString()
  //     })
  //     dispatch({ type: 'UPDATE_QUEUES', payload: data })
  //     toast.success('Nível do departamento atualizado!')
  //   } catch (err) {
  //     toastError(err)
  //   }
  // }

  const handleToggleFilter = () => {
    setShowOnlyLevel1(prev => {
      const newState = !prev
      if (!newState) {
        setEditingFlowQueueId(null) // NOVIDADE: Redefine selectedBotId quando o filtro é desativado
        setSelectedBotId(null)
      }
      return newState
    })
  }

  const handleShowAllBots = () => {
    setShowAllBots(prev => !prev)
    setSelectedBotId(null)
    setEditingFlowQueueId(null)
  }

  const filteredQueues = queues.filter(queue => {
    if (showAllBots) return true

    const selectedBot = botsWithWhatsApp.find(bot => bot.id === selectedBotId)

    if (selectedBot) {
      const botOptions = selectedBot.options || []
      const isLinkedToBot = botOptions.some(
        option => option.forwardQueueId === queue.id
      )
      return isLinkedToBot
    }
    return false
  })

  useEffect(() => {
    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => {
      window.removeEventListener('resize', checkOverflow)
    }
  }, [filteredQueues])

  const handleEditClosingReason = reason => {
    setSelectedClosingReason(reason)
    setClosingReasonModalOpen(true)
  }

  return (
    <ProtectedRoute
      requiredRoles={['super', 'admin', 'departamentos']}
      fallbackPath="/tickets"
    >
      <MainContainer aria-hidden={isMainContentHidden}>
        <ConfirmationModal
          title={
            deletingClosingReason
              ? `Excluir Assunto de Finalização`
              : selectedQueue
                ? `${i18n.t('queues.confirmationModal.deleteTitle')} ${selectedQueue.name
                }?`
                : ''
          }
          open={confirmModalOpen}
          onClose={handleCloseConfirmationModal}
          onConfirm={() => {
            if (deletingClosingReason) {
              handleDeleteClosingReason(deletingClosingReason.id)
            } else if (selectedQueue) {
              handleDeleteQueue(selectedQueue.id)
            }
          }}
        >
          {deletingClosingReason
            ? i18n.t('closingReasons.confirmationModal.deleteMessage')
            : i18n.t('queues.confirmationModal.deleteMessage')}
        </ConfirmationModal>
        <DepartmentModal
          open={departmentModalOpen}
          onClose={handleCloseDepartmentModal}
          option={selectedDepartment}
          onFlowUpdate={fetchQueues}
        />

        <QueueModal
          open={queueModalOpen}
          onClose={handleCloseQueueModal}
          queueId={selectedQueue?.id}
        />

        <ClosingReasonModal
          open={closingReasonModalOpen}
          onClose={handleCloseClosingReasonModal}
          closingReasonId={selectedClosingReason?.id}
        />
        <MainHeader
          title="Bot e Fluxo"
          description="Configure Departamentos para automatizar respostas e organizar o fluxo de atendimento de forma eficiente."
          showTutorial
        >
          <MainHeaderButtonsWrapper>
            <div className={classes.headerButtonsContainer}>
              <div className={classes.headerButtons}>
                <ButtonAddNew
                  onClick={handleOpenQueueModal}
                  startIcon={<BotIcon size={20} />}
                  ref={buttonRef}
                  label="Novo Bot"
                />
                {/* <Button
                  variant="contained"
                  onClick={handleOpenDepartmentModal}
                  className={classes.buttonNewBot}
                  startIcon={<Workflow />}
                  ref={buttonRef}
                >
                  Novo Departamento
                </Button> */}
                {requireClosingReason && (
                  <ButtonAddNew
                    onClick={handleOpenClosingReasonModal}
                    startIcon={<MessageCircleMore size={20} />}
                    ref={buttonRef}
                    label="Novo Assunto"
                  />
                )}
              </div>
              <div className={classes.headerButtonsSwitch}>
                <InputSwitch
                  checked={showAllBots}
                  onChange={handleShowAllBots}
                  color="primary"
                  size="small"
                  label="Ver todos os departamentos"
                />
                <InputSwitch
                  checked={showOnlyLevel1}
                  onChange={handleToggleFilter}
                  color="primary"
                  size="small"
                  label="Ativar Fluxo Avançado"
                />
              </div>
            </div>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        <Paper className={classes.mainPaper} elevation={0}>
          <Tabs
            value={activeTab}
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="on"
            variant="scrollable"
            onChange={handleTabChange}
            className={classes.tab}
          >
            <Tab label="Bots" value={'bots'} className={classes.tabHeader} />
            {requireClosingReason && (
              <Tab
                label="Assuntos"
                value={'closingReasons'}
                className={classes.tabHeader}
              />
            )}
          </Tabs>

          <TabPanel className={classes.container} value={activeTab} name={'bots'}>
            {!showAllBots && (
              <Grid container spacing={2} className={classes.botContainer}>
                {botsWithWhatsApp.map(queue => (
                  <Grid
                    item
                    xs={2}
                    className={classes.botCardGrid}
                    key={`bot-card-${queue.id}`}
                  >
                    <div
                      key={`bot-card-${queue.id}`}
                      className={`${classes.botCardContainer} ${selectedBotId === queue.id
                          ? classes.botCardContainerActive
                          : ''
                        }`}
                      onClick={() => handleSelectBot(queue.id)}
                    >
                      <div className={classes.botCardContent}>
                        <div className={classes.botCardHeader}>
                          <div className={classes.botHeaderTitle}>
                            <BotIcon size={24} />
                            <Typography
                              variant="h6"
                              className={classes.botCardTitle}
                            >
                              {queue.name}
                            </Typography>
                          </div>
                          <Typography
                            variant="body1"
                            className={classes.botWhatsAppNumber}
                          >
                            {queue.whatsapp
                              ? queue.whatsapp.number
                              : 'Sem Conexão'}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    <div className={classes.botContainerEdit}>
                      <IconButton
                        aria-label="Editar Bot"
                        onClick={e => {
                          e.stopPropagation()
                          handleEditQueue(queue)
                        }}
                        size="small"
                      >
                        <Pencil size={18} />
                      </IconButton>
                    </div>
                  </Grid>
                ))}
              </Grid>
            )}

            {(selectedBotId !== null || showAllBots) && (
              <>
                <Grid container spacing={2}>
                  {loading ? (
                    <Grid item xs={12}>
                      <Typography variant="body1" align="center">
                        Carregando...
                      </Typography>
                    </Grid>
                  ) : (
                    <div className={classes.departmentContainer}>
                      <div className={classes.departmentButtons}>
                        {filteredQueues.map(queue => (
                          <div
                            key={`dept-card-${queue.id}`}
                            className={classes.departmentCardGrid}
                          >
                            {/* Área do nome do Departamento (clicável para o Fluxo) */}
                            <div
                              onClick={() => handleEditFlow(queue)}
                              className={`${classes.departmentCardContent} ${editingFlowQueueId === queue.id
                                  ? classes.departmentCardContentActive
                                  : ''
                                }`}
                            >
                              <Typography variant="body1">
                                {queue.name}
                              </Typography>
                            </div>
                            {/* Botão de Editar (separado) */}
                            <div className={classes.departmentContainerEdit}>
                              <IconButton
                                aria-label="Editar Departamento"
                                onClick={e => {
                                  e.stopPropagation()
                                  handleEditDepartment(queue)
                                }}
                                size="small"
                              >
                                <Pencil size={18} />
                              </IconButton>
                            </div>
                          </div>
                        ))}
                      </div>
                      {showOnlyLevel1 && editingFlowQueueId && (
                        <Typography
                          variant="subtitle1"
                          className={classes.nameDepartment}
                        >
                          Você está editando o Fluxo:
                          <span>
                            {
                              queues.find(q => q.id === editingFlowQueueId)
                                ?.name
                            }
                          </span>
                        </Typography>
                      )}
                    </div>
                  )}
                </Grid>

                {showOnlyLevel1 && editingFlowQueueId && (
                  <Grid item xs={12}>
                    <DepartmentsFlow queueId={editingFlowQueueId} />
                  </Grid>
                )}
              </>
            )}
          </TabPanel>

          <TabPanel
            className={classes.container}
            value={activeTab}
            name={'closingReasons'}
          >
            <QuickTable
              data={closingReasons}
              columns={columns}
              loading={closingReasonsLoading}
              customCellRenderer={customCellRenderer}
              customActionsRenderer={customActionsRenderer}
              actions={['edit', 'delete']}
              containerProps={{
                className: classes.mainPaper,
                onScroll: handleScroll
              }}
            />
          </TabPanel>
        </Paper>
      </MainContainer>
    </ProtectedRoute>
  )
}

export default Departments
